import {Directive, inject} from "@angular/core";
import {BaseAppointmentComponent} from "./base-appointment.component";
import {Subscription} from "rxjs";
import {RecordingService} from "../../services/recording.service";
import {HttpClient} from "@angular/common/http";
import {PageLoaderService} from "../../services/page-loader.service";
import {SnackbarService} from "../../services/snackbar.service";
import {environment} from "../../../environments/environment";
import {RecordingStates} from "../../lib/enums";
import {PutAudioRequest} from "scribe-poc-shared";
import {UploadService} from "../../services/upload.service";
import {RecordingToUpload} from "../../lib/recording-to-upload";

/** Base class of components that need to take audio recordings */
@Directive()
export abstract class BaseRecordingComponent extends BaseAppointmentComponent {
	/** The current state of the recording process */
	private _state:RecordingStates = RecordingStates.Initial;

	/** Subscription for when audio recording errors */
	private errorSubscription:Subscription;

	/** Subscription for when audio recording is interrupted */
	private interruptedSubscription:Subscription;

	/** Controlling microphone interactions */
	protected recordingService = inject(RecordingService);

	/** Interaction with backend */
	protected http = inject(HttpClient);

	/** Showing loading spinner during loading */
	protected pageLoaderService = inject(PageLoaderService);

	/** Showing snackbar messages */
	protected snackbar = inject(SnackbarService);

	/** Service to perform non-blocking uploads with */
	protected uploadService = inject(UploadService);

	override async onNewAppointmentData() {
		if(await this.recordingService.testSupport()) {
			this.resetToDefaultState();
		} else {
			this.state = RecordingStates.Unsupported;
		}
	}

	/** Reset the recording process to the default (waiting or done) */
	protected abstract resetToDefaultState():void;

	override ngOnDestroy():void {
		super.ngOnDestroy();

		this.cancelRecording();
	}

	/** Start recording the user */
	async startMic() {
		if(await this.recordingService.startMic()) {
			this.state = RecordingStates.Recording;

			this.errorSubscription = this.recordingService.onErrorEvent.subscribe(() => this.onError());
			this.interruptedSubscription = this.recordingService.onInterruptedEvent.subscribe(() => this.onInterrupt());
		} else {
			this.state = RecordingStates.Error;
		}
	}

	/** Called when an error occurs in recording */
	private onError() {
		this.state = RecordingStates.Error;
		this.errorSubscription?.unsubscribe();
		this.interruptedSubscription?.unsubscribe();
	}

	/**
	 * Called when the audio stream was interrupted outside the control of the web app (by default this will stop the
	 * mic, override to change behavior).
	 */
	protected onInterrupt() {
		this.cancelRecording();
	}

	/** Cancel a recording, this will throw out a current recording that is in progress */
	cancelRecording() {
		this.errorSubscription?.unsubscribe();
		this.interruptedSubscription?.unsubscribe();
		this.recordingService.cancelMic();
	}

	/** Pause a recording session, which can be started again */
	pauseMic() {
		this.recordingService.pauseMic();
		this.state = RecordingStates.Paused;
	}

	/** Resume a paused recording session */
	resumeMic() {
		this.recordingService.resumeMic();
		this.state = RecordingStates.Recording;
	}

	/** Stop recording and close the stream */
	async stopMic() {
		this.pageLoaderService.show("Processing recording...");

		try {
			//stop recording, the final blob will be saved once the onStop event is called
			const result = await this.recordingService.stopMic();

			this.onStop(result);
		} finally {
			this.pageLoaderService.close();
		}
	}

	/**
	 * Called when the mic officially stops recording and processes the resulting audio
	 * @param result the final recording audio
	 * @param isUpload if this is a file upload the normal mic recording stuff will be skipped
	 */
	protected onStop(result:RecordingToUpload, isUpload:boolean = false) {
		let duration:number = 0;

		if(!isUpload) {
			//make sure we're running inside the Angular zone, as to pickup async changes
			this.errorSubscription.unsubscribe();
			this.interruptedSubscription.unsubscribe();

			duration = result.duration / 1000;

			//only upload if a recording is long enough
			if(duration < environment.recordingDurationThreshold) {
				this.showErrorAndReset(`Recording was too short: ${duration}`, "Your recording was too short to upload.");
				return;
			}
		}

		if(result.size < environment.recordingSizeThreshold) {
			this.showErrorAndReset(`File size was too small: ${result.size}`,
				"There was an error uploading your recording, it's too small.");
		} else {
			const request:PutAudioRequest = {
				...this.getAudioData(),
				audioLength: duration,
				appointmentId: this.appointment.id,
				mimeType: result.type
			};

			//upload the next part asynchronously
			this.uploadService.putAudioPart(request, result);

			//set the state
			this.state = RecordingStates.Done;

			//notify children
			this.putAudioPartComplete(duration);
		}
	}

	/** Get the request data to send to the backend */
	protected abstract getAudioData():AudioRequestData;

	/**
	 * Called when the putting the next audio part was completed successfully
	 * @param duration the final audio duration in seconds
	 */
	protected abstract putAudioPartComplete(duration:number):void;

	/**
	 * Show an error to the user and reset the recording state
	 * @param internalMessage an internal message to log to the console
	 * @param externalMessage an external message to show to the user in the snackbar
	 */
	private showErrorAndReset(internalMessage:string, externalMessage:string) {
		console.error(internalMessage);
		this.snackbar.error(externalMessage)
		this.pageLoaderService.close();
		this.resetToDefaultState();
	}

	/** Get the current state */
	get state() {
		return this._state;
	}

	/** Set the current state */
	set state(value:RecordingStates) {
		this._state = value;
	}

	/** Optionally get the previous recording's time in milliseconds (Default returns 0) */
	get previousRecordingTime() {
		return 0;
	}

	/** Return the total time of the recording in milliseconds */
	get recordingTime():number {
		// get the current time, only if we're recording or uploading (we don't have the final audio length yet from
		// the server)
		const currentTime =
			this.state == RecordingStates.Recording ||
			this.state == RecordingStates.Paused ?
				this.recordingService.recordingTime : 0;

		//add the previous time to the current time
		return this.previousRecordingTime + currentTime;
	}

	/** Is the recording / microphone in an error state? */
	get isError() {
		return this.state == RecordingStates.Error ||
			this.state == RecordingStates.NoDevices ||
			this.state == RecordingStates.Unsupported ||
			this.state == RecordingStates.MicDeclined;
	}
}

export type AudioRequestData = Omit<PutAudioRequest, "audioLength" | "mimeType" | "appointmentId">;
